import React, { useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { getLandingColors, landingEffectColors } from './theme';
import { Contact } from './Contact';
import { About } from './About';
import { Terms } from './Terms';
import { Privacy } from './Privacy';
import { Header } from './Header';
import { Footer } from './Footer';
// Боковые кадры веера (2026-10-18, из записей output/tiktok/clips-en, 9:16 как видео в центре) — то, чего НЕТ в ролике (владелица: «скрины справа и
// слева не должны повторять то, что в центре»): комната с котлом (другое зелье, чем в ролике; владелица: «лучше комнату с котлом вместо книги») и лавка в другом оформлении. Прежние — search-crystal-lake / shop-visitors.
import shotSearch from './Screenshot/cauldron-2026-10-18.webp';
import shotShop from './Screenshot/shop-decor-2026-10-18.webp';
import { AppleIcon, GooglePlayIcon } from './icons';
import { SparkleField } from './SparkleField';
import { STRINGS, isLanguage, normalizeLanguage, type Language } from './i18n';

// Ссылки на сторы: заполнить после публикации приложения.
const APP_STORE_URL = '#';
const GOOGLE_PLAY_URL = '#';
// В центре веера — ролик геймплея вместо скриншота (2026-10-18, владелица: «по центру, где 3 скриншота, вместо среднего — видео»):
// public/video, без звука, 15 с (поиск → миска → котёл → флакон → продажа), собран из записей output/tiktok/clips-en 2026-10-18.
const GAMEPLAY_VIDEO = '/video/gameplay.mp4';
const GAMEPLAY_POSTER = '/video/gameplay-poster.jpg';

const getDeviceLanguage = (): Language => normalizeLanguage(typeof navigator === 'undefined' ? 'en' : navigator.language);

const createCssVariables = (theme: ReturnType<typeof getLandingColors>) => {
  const rows = Object.entries({ ...theme, ...landingEffectColors })
    .map(([key, value]) => `  --${key}:${value};`)
    .join('\n');
  // Единая типографическая шкала лендинга. Размеры фиксированы в rem и НЕ
  // растут от ширины экрана (лёгкую адаптацию даёт только root font-size).
  return `:root{\n${rows}\n  --font-root: clamp(15px, 0.55vw + 10px, 24px);\n  --font-h1: 2.35rem;\n  --font-h2: 1.3rem;\n  --font-brand-name: 1rem;\n  --font-brand-subtitle: 0.52rem;\n  --font-feature-mark: 0.8rem;\n  --text-large: 1.08rem;\n  --text-body: 1rem;\n  --text-small: 0.88rem;\n  --text-caption: 0.78rem;\n}`;
};

const getStoredLanguage = (): Language | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem('landingLang');
    if (isLanguage(stored)) return stored;
  } catch {
    // ignore
  }
  return null;
};

// Тексты главной — src/locales/<язык>.ts → home (2026-10-24). 2026-10-17 (аудит сторов С10): «у каждого зелья рабочий эффект» → «у каждого свой рецепт, многие работают» — часть зелий только для продажи.

const baseStyles = `
*{box-sizing:border-box;}
html,body,#root{height:100%;min-height:100%;margin:0;}
/* Корневой размер растёт с шириной экрана — ВСЁ на странице задано в rem,
   поэтому на больших мониторах макет увеличивается пропорционально, как зум. */
html{font-size:var(--font-root);}
body{background:var(--color-transparent);color:var(--text);font-family:'Inter',system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;}
button,a{font-family:inherit;text-decoration:none;}
a{color:inherit;}

.landing{min-height:100vh;min-height:100svh;display:flex;flex-direction:column;isolation:isolate;}
.landing::before{content:'';position:fixed;inset:-18%;background:
  radial-gradient(circle at 18% 0%, var(--color-rgba-139-92-246-30), var(--color-transparent) 55%),
  radial-gradient(circle at 88% 12%, var(--color-rgba-245-158-11-20), var(--color-transparent) 50%),
  radial-gradient(circle at 55% 112%, var(--color-rgba-109-40-217-26), var(--color-transparent) 55%),
  radial-gradient(circle at 12% 68%, var(--color-rgba-52-211-153-16), var(--color-transparent) 58%),
  radial-gradient(circle at 92% 80%, var(--color-rgba-52-211-153-10), var(--color-transparent) 50%),
  var(--background);
  transform:translate3d(0,0,0);
  will-change:transform;
  animation:bg-drift 22s ease-in-out infinite alternate;
  z-index:-2;
  pointer-events:none;}
/* Искры фона рисует SparkleField.tsx (Babylon) — канвас под контентом. */
.sparkles{position:fixed;inset:0;width:100%;height:100%;z-index:-1;pointer-events:none;mix-blend-mode:screen;}

@keyframes bg-drift{
  0%{transform:translate3d(0,0,0) scale(1);}
  50%{transform:translate3d(-1.4%, 1.1%, 0) scale(1.02);}
  100%{transform:translate3d(1.6%, -1.2%, 0) scale(1.03);}
}

@media (prefers-reduced-motion: reduce){
  .landing::before{
    animation:none !important;
  }
}

.header{display:flex;align-items:center;justify-content:space-between;gap:1rem;padding:0.8rem clamp(1rem,3vw,2.5rem);background:var(--color-transparent);z-index:10;width:100%;}
.brand{display:flex;align-items:center;gap:0.65rem;cursor:pointer;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;}
.brand__logo{width:2.9rem;height:2.9rem;display:flex;align-items:center;justify-content:center;}
.brand__logo img{width:100%;height:100%;object-fit:contain;display:block;padding:0.28rem;filter:drop-shadow(0 0 10px var(--color-black-a-45));}
.brand__name{font-size:var(--font-brand-name);}
.brand__name-img{display:block;height:1.65rem;width:auto;filter:drop-shadow(0 1px 4px var(--color-black-a-45));}
.brand__subtitle{font-size:var(--font-brand-subtitle);font-weight:400;color:var(--textSecondary);letter-spacing:0.34em;margin-top:0.2rem;white-space:nowrap;}
.header__nav{display:flex;gap:1rem;font-size:var(--text-small);}
.header__nav a{color:var(--textSecondary);text-transform:uppercase;letter-spacing:0.04em;font-weight:600;}
.header__nav a:hover{color:var(--primary);}
.header__actions{display:flex;align-items:center;gap:0.55rem;flex-wrap:wrap;}
.header__contact{display:flex;align-items:center;gap:0.55rem;}
.header__contact svg{flex:0 0 auto;}
.language-switch{display:flex;gap:0.35rem;background:var(--color-white-a-4);border-radius:999px;padding:0.25rem;border:1px solid var(--color-white-a-10);}
.language-switch .lang{border:none;background:var(--color-transparent);color:var(--textTertiary);padding:0.3rem 0.7rem;border-radius:999px;font-size:var(--text-caption);cursor:pointer;font-weight:600;}
.language-switch .lang.active{background:var(--onPrimary);color:var(--primaryDark);}
/* 13 языков (2026-10-24): список вместо кнопок; стрелка — свой значок, у системной разный вид в браузерах */
.lang-select{appearance:none;-webkit-appearance:none;border:none;background:var(--color-transparent);color:var(--text);font:inherit;font-size:var(--text-caption);font-weight:600;padding:0.3rem 1.7rem 0.3rem 0.7rem;border-radius:999px;cursor:pointer;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23c9b8f0' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 0.65rem center;}
.lang-select:focus-visible{outline:2px solid var(--primary);outline-offset:2px;}
.lang-select option{color:var(--primaryDark);background:var(--onPrimary);}
.visually-hidden{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;}
.btn{border-radius:999px;padding:0.55rem 1.35rem;font-size:var(--text-small);border:1px solid var(--color-white-a-20);background:var(--color-transparent);color:var(--text);cursor:pointer;font-weight:600;transition:all 0.2s ease;}
.btn--primary{border:none;background:linear-gradient(135deg,var(--primary),var(--primaryDark));color:var(--onPrimary);font-weight:700;box-shadow:0 6px 18px var(--color-rgba-109-40-217-40);}
.btn--primary:hover{filter:brightness(1.04);}
.btn--store{display:inline-flex;align-items:center;gap:0.6rem;border:1px solid var(--color-white-a-22);background:var(--color-black-a-35);color:var(--text);font-weight:700;padding:0.65rem 1.4rem;font-size:var(--text-body);}
.btn--store:hover{border-color:var(--primaryLight);background:var(--color-rgba-139-92-246-12);}
.btn--store svg{flex:0 0 auto;}
.btn--secondary{border:1px solid var(--color-white-a-16);background:var(--color-black-a-12);color:var(--text);font-weight:600;}
.btn--secondary:hover{background:var(--color-black-a-18);}

.footer{margin-top:auto;}

.main{flex:1;display:flex;flex-direction:column;gap:clamp(1.4rem,2.6vh,2rem);padding:clamp(0.9rem,2.4vh,1.6rem) clamp(1.1rem,5vw,3rem) clamp(0.9rem,2.4vh,1.6rem);min-height:0;width:100%;}
.hero{display:grid;grid-template-columns:1.06fr 0.94fr;gap:clamp(1.6rem,2.6vw,2.2rem);align-items:stretch;flex:1;min-height:0;padding:0;background:none;border:none;border-radius:0;box-shadow:none;}
@media(max-width:980px){.hero{grid-template-columns:1fr;}}
.hero__copy{display:grid;grid-template-rows:auto minmax(0,1fr);gap:0.9rem;min-height:0;width:100%;max-width:54rem;justify-self:start;}
.hero__copy-top{align-self:start;}
.hero__copy-main{display:flex;flex-direction:column;justify-content:center;gap:0.95rem;min-height:0;}
.hero__top-pills{display:flex;flex-wrap:wrap;gap:0.6rem;}
.hero__top-pill{padding:0.32rem 0.82rem;border-radius:999px;border:1px solid var(--color-white-a-18);font-size:var(--text-caption);font-weight:600;color:var(--textSecondary);background:var(--color-black-a-12);}
.hero__title{margin:0;font-size:var(--font-h1);line-height:1.15;background:linear-gradient(180deg,var(--color-white) 35%,var(--color-c9b8f0) 100%);-webkit-background-clip:text;background-clip:text;color:var(--color-transparent);}
.hero__description{margin:0;font-size:var(--text-body);color:var(--textSecondary);line-height:1.65;max-width:48rem;}
.hero__actions{display:flex;gap:0.9rem;flex-wrap:wrap;}
.hero__actions--soon{position:relative;padding-top:1rem;}
.hero__soon-badge{position:absolute;top:-0.15rem;left:0.6rem;z-index:2;background:linear-gradient(135deg,var(--primary),var(--primaryDark));color:var(--onPrimary);font-size:var(--text-caption);font-weight:700;padding:0.3rem 0.85rem;border-radius:999px;box-shadow:0 4px 14px var(--color-black-a-45);white-space:nowrap;transform:rotate(-3deg);}
.btn--store.is-disabled{opacity:0.5;filter:grayscale(0.35);cursor:not-allowed;pointer-events:none;}
.btn--store.is-disabled:hover{transform:none;}
.features{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:0.9rem;}
@media(max-width:1100px){.features{grid-template-columns:1fr 1fr;}}
@media(max-width:600px){.features{grid-template-columns:1fr;}}
.hero__feature{display:flex;flex-direction:column;gap:0.25rem;background:var(--color-white-a-3-5);border:1px solid var(--color-white-a-8);border-radius:0.9rem;padding:0.75rem 0.95rem;transition:border-color 0.2s ease,background 0.2s ease;}
.hero__feature:hover{border-color:var(--color-rgba-139-92-246-45);background:var(--color-rgba-139-92-246-8);}
.hero__feature-title{font-weight:700;font-size:var(--text-body);color:var(--text);}
.hero__feature-title::before{content:'\\2726';color:var(--secondaryLight);margin-right:0.45rem;font-size:var(--font-feature-mark);}
.hero__feature-desc{margin:0;font-size:var(--text-small);color:var(--textSecondary);line-height:1.55;}
.hero__panel{background:none;border:none;padding:0;display:flex;flex-direction:column;gap:0.7rem;box-shadow:none;align-self:stretch;height:auto;min-height:0;}
.tags{display:flex;flex-wrap:wrap;gap:0.5rem;}
.tag-pill{border:1px solid var(--color-white-a-12);border-radius:999px;padding:0.32rem 0.82rem;font-size:var(--text-caption);color:var(--textSecondary);background:var(--color-black-a-10);}

@media(max-width:540px){
  .header__contact{padding:0.55rem;width:2.55rem;height:2.55rem;justify-content:center;}
  .header__contact-label{display:none;}
}


/* Веер из трёх скриншотов игры (заменил 3D-мини-игру; её код остался в
   CollectorGame.tsx / collectorScene.ts на случай возврата): несколько кадров
   видны одновременно, без имитации телефона. */
.shotfan{position:relative;height:100%;min-height:22rem;display:flex;align-items:center;justify-content:center;}
.shotfan__img{height:clamp(15rem,24vw + 6rem,34rem);aspect-ratio:598/1300;object-fit:cover;border-radius:1.1rem;border:1px solid var(--color-white-a-14);box-shadow:0 28px 60px var(--color-black-a-70),0 10px 22px var(--color-black-a-55),0 2px 6px var(--color-black-a-45);flex:0 0 auto;}
.shotfan__img--left{transform:rotate(-7deg) translateY(4%);margin-right:-1.8rem;z-index:1;}
.shotfan__img--center{z-index:2;height:clamp(16.5rem,24vw + 7.5rem,36rem);}
.shotfan__img--right{transform:rotate(7deg) translateY(4%);margin-left:-1.8rem;z-index:1;}
/* Видео в центре веера (2026-10-18): рамка по пропорции ролика (9:16), а не кадров (598:1300) — иначе края ролика срезались. */
/* все три карточки — 9:16, как ролик и кадры игры: ничего не обрезается */
.shotfan__img{aspect-ratio:9/16;}
video.shotfan__img{display:block;background:var(--color-black-a-35);object-fit:contain;}
/* Адаптив (2026-10-18): в одну колонку (≤980 px) веер рос от 24vw и на планшете был мелким (видео ~300 px при экране 1024) —
   теперь от ширины экрана; на телефоне шапка не рассыпается столбиком: кнопки — одной строкой под логотипом. */
@media(max-width:980px){
  .shotfan{min-height:0;padding:0.6rem 0 1.2rem;}
  .shotfan__img{height:clamp(15rem,50vw,30rem);}
  .shotfan__img--center{height:clamp(16.5rem,56vw,33rem);}
}
/* самые узкие телефоны: карточки 9:16 шире прежних — веер чуть ниже, чтобы не упирался в края экрана */
@media(max-width:360px){
  .shotfan__img{height:12.5rem;}
  .shotfan__img--center{height:14rem;}
}
@media(max-width:540px){
  /* Шапка телефона (2026-10-19, владелица: «меню плохо выглядит»): две ровные строки вместо логотипа одного в строке и растянутых
     по краям кнопок — «логотип · Связаться» / «Об игре · языки». */
  .header{display:grid;grid-template-columns:minmax(0,1fr) auto;grid-template-areas:'brand contact' 'nav lang';align-items:center;row-gap:0.7rem;column-gap:0.6rem;}
  .header__actions{display:contents;}
  .brand{grid-area:brand;min-width:0;}
  .header__contact{grid-area:contact;justify-self:end;}
  .header__nav{grid-area:nav;justify-self:start;}
  .language-switch{grid-area:lang;justify-self:end;}
  .header__nav a{display:inline-block;padding:0.45rem 0.95rem;border-radius:999px;border:1px solid var(--color-white-a-10);background:var(--color-white-a-4);font-size:var(--text-caption);}
  /* подпись под логотипом была 7,8 px — на телефоне не прочитать */
  .brand__subtitle{font-size:0.7rem;letter-spacing:0.24em;}
  /* Веер на телефоне был шире экрана (~360 px при месте ~342 px на ширине 375) — размер от ширины экрана, карточки сильнее
     заходят друг на друга: вместе с наклоном ≈ 80 % ширины. */
  .shotfan__img{height:52vw;}
  .shotfan__img--center{height:60vw;}
  .shotfan__img--left{margin-right:-10vw;}
  .shotfan__img--right{margin-left:-10vw;}
}
/* Ничего не шире экрана телефона (2026-10-19): длинные адреса почты и ссылки переносятся, колонки сетки могут сжиматься. */
body{overflow-wrap:break-word;}
.landing a{overflow-wrap:anywhere;}
.hero > *{min-width:0;}
.landing{overflow-x:clip;}
`;

const HomeContent: React.FC<{ t: (typeof STRINGS)['en']['home']; language: Language }> = ({
  t,
  language,
}) => (
  <main className="main">
    <section className="hero" id="hero">
      <div className="hero__copy">
        <div className="hero__copy-top">
          <div className="hero__top-pills">
            <span className="hero__top-pill">{t.tags.search}</span>
            <span className="hero__top-pill">{t.tags.craft}</span>
            <span className="hero__top-pill">{t.tags.shop}</span>
          </div>
        </div>
        <div className="hero__copy-main">
          <h1 className="hero__title">{t.heroTitle}</h1>
          <p className="hero__description">{t.heroDescription}</p>
          {/* Игра ещё не вышла — ссылки на сторы (APP_STORE_URL/GOOGLE_PLAY_URL)
              пока заглушки ('#'). Кнопки оставлены для вида (бренд-иконки узнаваемы),
              но задизейблены, и поверх — бейдж "скоро" (решение пользователя 2026-08-12). */}
          <div className="hero__actions hero__actions--soon">
            <span className="hero__soon-badge">{t.comingSoon}</span>
            <a className="btn btn--store is-disabled" href={APP_STORE_URL} aria-disabled="true" onClick={(e) => e.preventDefault()}>
              <AppleIcon size={22} />
              <span>{t.appStore}</span>
            </a>
            <a className="btn btn--store is-disabled" href={GOOGLE_PLAY_URL} aria-disabled="true" onClick={(e) => e.preventDefault()}>
              <GooglePlayIcon size={20} />
              <span>{t.googlePlay}</span>
            </a>
          </div>
        </div>
      </div>
      <div className="hero__panel">
        <div className="shotfan">
          <img className="shotfan__img shotfan__img--left" src={shotSearch} alt="" />
          <video
            className="shotfan__img shotfan__img--center"
            src={GAMEPLAY_VIDEO}
            poster={GAMEPLAY_POSTER}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
          />
          <img className="shotfan__img shotfan__img--right" src={shotShop} alt="" />
        </div>
      </div>
    </section>
    <section className="features">
      {t.features.map((feature) => (
        <div key={feature.title} className="hero__feature">
          <div className="hero__feature-title">{feature.title}</div>
          <p className="hero__feature-desc">{feature.desc}</p>
        </div>
      ))}
    </section>
  </main>
);

const Home: React.FC<{
  language: Language;
  onLanguageChange: (lang: Language) => void;
}> = ({ language, onLanguageChange }) => {
  const t = STRINGS[language].home;
  return (
    <div className="landing landing--home">
      <SparkleField />
      <Header language={language} onLanguageChange={onLanguageChange} />
      <HomeContent t={t} language={language} />
      <Footer language={language} />
    </div>
  );
};

export const App: React.FC = () => {
  const [language, setLanguage] = useState<Language>(() => getStoredLanguage() ?? getDeviceLanguage());
  const colors = useMemo(() => getLandingColors(true), []);

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    try {
      window.localStorage.setItem('landingLang', lang);
    } catch {
      // ignore
    }
  };

  return (
    <BrowserRouter>
      <style>{createCssVariables(colors)}</style>
      <style>{baseStyles}</style>
      <Routes>
        <Route path="/" element={<Home language={language} onLanguageChange={handleLanguageChange} />} />
        <Route path="/contact/:lang" element={<Contact />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about/:lang" element={<About />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms/:lang" element={<Terms />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy/:lang" element={<Privacy />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<Home language={language} onLanguageChange={handleLanguageChange} />} />
      </Routes>
    </BrowserRouter>
  );
};
